<template>
  <div class="landing">
    <!-- Hero -->
    <section class="hero">
      <div class="hero-inner container">
        <div class="hero-text">
          <h1 class="hero-title">
            Supercharge Your Discord With Carl-bot.
          </h1>
          <p class="hero-subtitle">
            An All-in-one discord bot<template v-if="stats && stats.guilds">, trusted by <strong>{{ stats.guilds }}</strong> servers</template>.
          </p>
          <div class="hero-cta">
            <button
              type="button"
              v-on:click="addToDiscord"
              class="cta-primary"
            >
              <i class="fab fa-discord mr-2"></i>Add to Discord
            </button>
            <b-button
              v-if="logged_in"
              to="/servers"
              pill
              class="cta-secondary"
            >
              <font-awesome-icon icon="cog" class="mr-2" />Open Dashboard
            </b-button>
          </div>

        </div>

        <div class="hero-visual" v-if="!isMobile">
          <img
            :src="mascot"
            class="hero-mascot"
            alt="Carl-bot mascot"
            width="320"
            height="320"
            fetchpriority="high"
            decoding="async"
          />
        </div>
      </div>
    </section>

    <div class="below-hero">
    <!-- Features -->
    <section class="features container">
      <div class="section-heading">
        <h2 class="section-title">Why Carl-bot?</h2>
      </div>

      <div class="feature-grid">
        <a
          v-for="f in features"
          :key="f.title"
          :href="f.docs"
          :target="isExternal(f.docs) ? '_blank' : '_self'"
          :rel="isExternal(f.docs) ? 'noopener noreferrer' : null"
          class="feature-card"
        >
          <div class="feature-icon" :style="{ background: f.color }">
            <i :class="f.icon"></i>
          </div>
          <h3 class="feature-title">{{ f.title }}</h3>
          <p class="feature-desc">{{ f.desc }}</p>
          <span class="feature-link">
            Learn more <i class="fas fa-arrow-right ml-1"></i>
          </span>
        </a>
      </div>
    </section>

    <!-- Bottom CTA -->
    <section class="cta-band container">
      <div class="cta-inner">
        <h2 class="cta-title">Ready to upgrade your server?</h2>
        <p class="cta-text">
          Join millions of communities already running on Carl-bot.
        </p>
        <div class="cta-buttons">
          <button type="button" v-on:click="addToDiscord" class="cta-primary">
            <i class="fab fa-discord mr-2"></i>Add to Discord
          </button>
          <a
            href="https://docs.carl.gg/"
            target="_blank"
            rel="noopener noreferrer"
            class="cta-secondary"
          >
            <i class="fas fa-book mr-2"></i>Read the docs
          </a>
        </div>
      </div>
    </section>

    <!-- Footer -->
    <footer class="footer">
      <b-container>
        <b-row>
          <b-col md="6">
            <h6 class="tinytitle">Carl.gg</h6>
            <p class="footer-line">
              A service provided by
              <a href="https://botlabs.gg">BotLabs.gg</a> · All Rights Reserved
            </p>
          </b-col>
          <b-col md="6">
            <div class="footer-links">
              <b-link to="/terms-of-use" class="footer_title">Terms of Use</b-link>
              <a href="https://botlabs.gg/privacy-policy" class="footer_title">Privacy Policy</a>
              <a href="https://botlabs.gg/copyright-dispute-policy" class="footer_title">Copyright Dispute</a>
              <a href="https://botlabs.gg/gdpr-privacy-notice" class="footer_title">GDPR Notice</a>
              <a href="https://botlabs.gg/cookie-policy" class="footer_title">Cookie Policy</a>
              <a href="https://botlabs.gg/end-user-license-agreement" class="footer_title">EULA</a>
            </div>
          </b-col>
        </b-row>
      </b-container>
    </footer>
    </div>
  </div>
</template>

<script>
export default {
  name: "goaway",
  data() {
    return {
      features: [
        {
          title: "Reaction Roles",
          desc:
            "Up to 250 reaction-roles pairs per server with unique, verify, reversed, binding and temporary modes.",
          icon: "fas fa-smile",
          color: "#34353d",
          docs: "https://docs.carl.gg/#/roles?id=reaction-roles",
        },
        {
          title: "Moderation",
          desc:
            "Modlogs, timed punishments, sticky roles, full infraction history, everything your mod team needs.",
          icon: "fas fa-shield-alt",
          color: "#ff6b6b",
          docs: "https://docs.carl.gg/#/moderation",
        },
        {
          title: "Automod",
          desc:
            "Block bad links, spam, mass mentions, attachment spam and slurs. Per-rule punishments, custom rate limits and channel whitelists.",
          icon: "fas fa-robot",
          color: "#00c2a8",
          docs: "https://docs.carl.gg/#/automod",
        },
        {
          title: "Logging",
          desc:
            "Deleted and edited messages, joins, leaves, role changes, bans, invites; split across channels to keep things tidy.",
          icon: "fas fa-clipboard-list",
          color: "#4d8cff",
          docs: "https://docs.carl.gg/#/logging",
        },
        {
          title: "Custom Commands",
          desc:
            "Make your own commands and features using TagScript",
          icon: "fas fa-terminal",
          color: "#f8cb00",
          docs: "https://docs.carl.gg/#/tagstriggers?id=advanced-usage",
        },
        {
          title: "Suggestions",
          desc:
            "Let members suggest ideas and vote on them. Anonymous mode, mod responses and a dedicated decision log.",
          icon: "fas fa-lightbulb",
          color: "#ffb000",
          docs: "https://docs.carl.gg/#/suggestions",
        },
        {
          title: "Greetings",
          desc:
            "Greet new members with rich embeds and variables. Send a separate DM, set a farewell, and announce bans cleanly.",
          icon: "fas fa-door-open",
          color: "#ff7eb6",
          docs: "https://docs.carl.gg/#/greetings",
        },
        {
          title: "Permissions",
          desc:
            "Per-command rules, bulk management on the dashboard, global overrides and redirected output so the bot never gets annoying.",
          icon: "fas fa-key",
          color: "#9e80ff",
          docs: "https://docs.carl.gg/#/config?id=managing-commands",
        },
        {
          title: "Engagement",
          desc:
            "Starboard, sticky messages, fun commands like 8ball, roll and toproll to keep your community active.",
          icon: "fas fa-star",
          color: "#00c2ff",
          docs: "https://docs.carl.gg/#/fun",
        },
        {
          title: "Notifications",
          desc:
            "Lightning-fast Twitch and YouTube alerts so your server never misses a stream or new upload.",
          icon: "fas fa-bell",
          color: "#9146ff",
          docs: "https://docs.carl.gg/#/notifications",
        },
        {
          title: "Gaming",
          desc:
            "Get notified the moment a game goes free or on a sale across major stores.",
          icon: "fas fa-gamepad",
          color: "#5ad575",
          docs: "https://docs.carl.gg/#/games",
        },
        {
          title: "Server Discovery",
          desc:
            "List your community in our public server directory and reach members already looking for servers like yours.",
          icon: "fas fa-globe",
          color: "#5fa8ff",
          docs: "/server-discovery",
        },
        {
          title: "Premium",
          desc:
            "Custom Avatar and Banner, Higher limits, More reaction roles, More triggers, sticky messages, levels, temp channels, more of everything for serious servers.",
          icon: "fas fa-crown",
          color: "#f8cb00",
          docs: "/get-premium",
        },
      ],
      mascot: window.__MASCOT__ || (() => {
        if (Math.random() < 0.35) return "img/brand/googleturtle.svg";
        const kitchen = [
          "img/mascots/kitchen/bat-turtle.png",
          "img/mascots/kitchen/bell-turtle.png",
          "img/mascots/kitchen/bird-turtle.png",
          "img/mascots/kitchen/biting_lip-turtle.png",
          "img/mascots/kitchen/blush-turtle.png",
          "img/mascots/kitchen/cherries-turtle.png",
          "img/mascots/kitchen/city_sunrise-turtle.png",
          "img/mascots/kitchen/cupid-turtle.png",
          "img/mascots/kitchen/dog-turtle.png",
          "img/mascots/kitchen/dove_of_peace-turtle.png",
          "img/mascots/kitchen/drooling_face-turtle.png",
          "img/mascots/kitchen/eyes-turtle.png",
          "img/mascots/kitchen/face_with_cowboy_hat-turtle.png",
          "img/mascots/kitchen/face_with_hand_over_mouth-turtle.png",
          "img/mascots/kitchen/face_with_monocle-turtle.png",
          "img/mascots/kitchen/fireworks-turtle.png",
          "img/mascots/kitchen/fried_egg-turtle.png",
          "img/mascots/kitchen/gift-turtle.png",
          "img/mascots/kitchen/gift_heart-turtle.png",
          "img/mascots/kitchen/goal_net-turtle.png",
          "img/mascots/kitchen/grin-turtle.png",
          "img/mascots/kitchen/grinning-turtle.png",
          "img/mascots/kitchen/head_shaking_vertically-turtle.png",
          "img/mascots/kitchen/headphones-turtle.png",
          "img/mascots/kitchen/heart_eyes-turtle.png",
          "img/mascots/kitchen/heartpulse-turtle.png",
          "img/mascots/kitchen/hearts-turtle.png",
          "img/mascots/kitchen/hibiscus-turtle.png",
          "img/mascots/kitchen/hushed-turtle.png",
          "img/mascots/kitchen/index_pointing_at_the_viewer-turtle.png",
          "img/mascots/kitchen/innocent-turtle.png",
          "img/mascots/kitchen/kissing-turtle.png",
          "img/mascots/kitchen/kissing_closed_eyes-turtle.png",
          "img/mascots/kitchen/kissing_heart-turtle.png",
          "img/mascots/kitchen/kissing_smiling_eyes-turtle.png",
          "img/mascots/kitchen/laughing-turtle.png",
          "img/mascots/kitchen/love_letter-turtle.png",
          "img/mascots/kitchen/mirror_ball-turtle.png",
          "img/mascots/kitchen/mortar_board-turtle.png",
          "img/mascots/kitchen/muscle-turtle.png",
          "img/mascots/kitchen/nerd_face-turtle.png",
          "img/mascots/kitchen/notes-turtle.png",
          "img/mascots/kitchen/parachute-turtle.png",
          "img/mascots/kitchen/partying_face-turtle.png",
          "img/mascots/kitchen/raccoon-turtle.png",
          "img/mascots/kitchen/relaxed-turtle.png",
          "img/mascots/kitchen/relieved-turtle.png",
          "img/mascots/kitchen/rose-turtle.png",
          "img/mascots/kitchen/saluting_face-turtle.png",
          "img/mascots/kitchen/ski-turtle.png",
          "img/mascots/kitchen/slightly_smiling_face-turtle.png",
          "img/mascots/kitchen/smile-turtle.png",
          "img/mascots/kitchen/smiley-turtle.png",
          "img/mascots/kitchen/smiling_face_with_3_hearts-turtle.png",
          "img/mascots/kitchen/smirk-turtle.png",
          "img/mascots/kitchen/soccer-turtle.png",
          "img/mascots/kitchen/sparkling_heart-turtle.png",
          "img/mascots/kitchen/star_struck-turtle.png",
          "img/mascots/kitchen/stuck_out_tongue_closed_eyes-turtle.png",
          "img/mascots/kitchen/stuck_out_tongue_winking_eye-turtle.png",
          "img/mascots/kitchen/sunglasses-turtle.png",
          "img/mascots/kitchen/sunrise_over_mountains-turtle.png",
          "img/mascots/kitchen/tulip-turtle.png",
          "img/mascots/kitchen/turtle-balloon.png",
          "img/mascots/kitchen/turtle-birthday.png",
          "img/mascots/kitchen/turtle-blossom.png",
          "img/mascots/kitchen/turtle-bouquet.png",
          "img/mascots/kitchen/turtle-cheese_wedge.png",
          "img/mascots/kitchen/turtle-coffee.png",
          "img/mascots/kitchen/turtle-crown.png",
          "img/mascots/kitchen/turtle-deer.png",
          "img/mascots/kitchen/turtle-disguised_face.png",
          "img/mascots/kitchen/turtle-earth_africa.png",
          "img/mascots/kitchen/turtle-llama.png",
          "img/mascots/kitchen/turtle-pineapple.png",
          "img/mascots/kitchen/turtle-rainbow.png",
          "img/mascots/kitchen/turtle-star2.png",
          "img/mascots/kitchen/two_hearts-turtle.png",
          "img/mascots/kitchen/unicorn_face-turtle.png",
          "img/mascots/kitchen/wink-turtle.png",
          "img/mascots/kitchen/yawning_face-turtle.png",
          "img/mascots/kitchen/yum-turtle.png",
        ];
        return kitchen[Math.floor(Math.random() * kitchen.length)];
      })(),
    };
  },
  methods: {
    isExternal(url) {
      return typeof url === "string" && /^https?:\/\//i.test(url);
    },
    login() {
      window.location.href = process.env.VUE_APP_DOMAIN + "/api/login";
    },
    addToDiscord() {
      if (this.logged_in) {
        window.location.href = this.discordInviteUrl;
        return;
      }
      try {
        window.sessionStorage.setItem("pendingAddToDiscord", "1");
      } catch (e) {
        /* sessionStorage may be unavailable; fall through to login */
      }
      this.login();
    },
    updateAuthInfo(e) {
      if (e.data.discriminator != undefined) {
        this.$store.commit("login", e.data);
      }
      window.removeEventListener("message", this.updateAuthInfo);
      this.$router.push("/servers");
    },
  },
  computed: {
    logged_in() {
      return Object.keys(this.$store.state.discord_user).length !== 0;
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        navigator.userAgent
      );
    },
    stats() {
      return this.$store.state.stats;
    },
    discordInviteUrl() {
      return "https://discord.com/oauth2/authorize?&client_id=235148962103951360&scope=applications.commands+bot&permissions=2088234230&response_type=code&redirect_uri=https://carl.gg/api/server_auth";
    },
  },
  beforeCreate() {
    this.axios.get("/api/v1/botstats").then((r) => {
      if (!r.data.logged_in) {
        this.$store.commit("logout");
      }
      this.$store.commit("sync_stats", r.data);
      let pending = false;
      try {
        pending = window.sessionStorage.getItem("pendingAddToDiscord") === "1";
      } catch (e) {
        pending = false;
      }
      if (pending && r.data.logged_in) {
        try {
          window.sessionStorage.removeItem("pendingAddToDiscord");
        } catch (e) {
          /* ignore */
        }
        window.location.href = this.discordInviteUrl;
      }
    });
  },
};
</script>

<style scoped>
.landing {
  color: #fefefe;
  font-family: "Poppins", "Roboto", sans-serif;
  overflow-x: hidden;
}

.below-hero {
  position: relative;
}

.below-hero::before {
  content: "";
  position: absolute;
  top: 0;
  bottom: 0;
  left: calc(-50vw + 50%);
  width: 100vw;
  background: #2f353a;
  z-index: -1;
}

/* ---------- Hero ---------- */
.hero {
  position: relative;
  overflow: hidden;
}

.hero-inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 60px;
}

.hero-text {
  flex: 1;
  max-width: 620px;
}

.hero-brand {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 3px;
  text-transform: uppercase;
  color: #ffffff;
  margin-bottom: 12px;
}

.hero-title {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 48px;
  line-height: 1.1;
  color: #ffffff;
  letter-spacing: -1.5px;
  max-width: 560px;
}

.gradient-text {
  color: #ffffff;
}

.hero-subtitle {
  font-size: clamp(13px, 2.6vw, 18px);
  line-height: 1.5;
  color: #ffffff;
  margin-bottom: 22px;
  font-weight: 500;
  white-space: nowrap;
}

.hero-cta {
  display: flex;
  flex-direction: row;
  gap: 14px;
  margin-bottom: 0;
}

.hero-cta .cta-primary,
.hero-cta .cta-secondary {
  width: 250px;
  justify-content: center;
  text-align: center;
}

/* When only one CTA is rendered (logged-out state), match the combined width of both. */
.hero-cta .cta-primary:only-child,
.hero-cta .cta-secondary:only-child {
  width: 514px;
}

@media screen and (max-width: 640px) {
  .hero-cta {
    flex-direction: column;
    align-items: center;
  }
  .hero-cta .cta-primary:only-child,
  .hero-cta .cta-secondary:only-child {
    width: 250px;
  }
}

.cta-primary {
  background: #ffffff !important;
  border: none !important;
  color: #1a1430 !important;
  padding: 14px 30px !important;
  font-weight: 600;
  font-size: 16px;
  border-radius: 999px !important;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.25);
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
}

.cta-primary:hover {
  transform: translateY(-2px);
  background: #f0eef8 !important;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.35);
  color: #1a1430 !important;
  text-decoration: none;
}

.cta-secondary {
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: #fff;
  padding: 13px 28px;
  font-weight: 600;
  font-size: 16px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  backdrop-filter: blur(10px);
  transition: background 0.2s ease, border-color 0.2s ease;
}

.cta-secondary:hover {
  background: rgba(255, 255, 255, 0.12);
  border-color: rgba(255, 255, 255, 0.35);
  color: #fff;
  text-decoration: none;
}

.hero-trust-inline {
  display: block;
  font-size: 18px;
  font-weight: 500;
  color: #ffffff;
  margin-top: 10px;
  letter-spacing: -0.2px;
  opacity: 0.92;
}

.hero-trust-inline strong {
  font-weight: 700;
}

/* Hero mascot */
.hero-visual {
  position: relative;
  flex-shrink: 0;
  width: 320px;
  height: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.hero-mascot {
  width: 100%;
  height: 100%;
  object-fit: contain;
  will-change: transform;
  animation: float 6s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translate3d(0, 0, 0); }
  50% { transform: translate3d(0, -12px, 0); }
}

/* ---------- Features ---------- */
.features {
  padding: 40px 15px 60px;
}

.section-heading {
  text-align: center;
  margin-bottom: 36px;
}

.section-title {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 40px;
  color: #fff;
  margin-bottom: 14px;
  letter-spacing: -0.5px;
}

.feature-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 22px;
}

.feature-card {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  padding: 28px;
  text-decoration: none;
  color: inherit;
  transition: transform 0.25s ease, border-color 0.25s ease, background 0.25s ease;
  display: flex;
  flex-direction: column;
  position: relative;
  overflow: hidden;
}

.feature-card::before {
  display: none;
}

.feature-card:hover {
  transform: translateY(-4px);
  border-color: rgba(158, 128, 255, 0.4);
  background: rgba(255, 255, 255, 0.06);
  text-decoration: none;
  color: inherit;
}

.feature-card:hover::before {
  opacity: 1;
}

.feature-icon {
  width: 52px;
  height: 52px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #fff;
  margin-bottom: 20px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.3);
}

.feature-title {
  font-family: "Poppins", sans-serif;
  font-weight: 600;
  font-size: 20px;
  color: #fff;
  margin-bottom: 10px;
}

.feature-desc {
  font-size: 14.5px;
  line-height: 1.55;
  color: #dbdae8;
  margin-bottom: 18px;
  flex-grow: 1;
}

.feature-link {
  font-size: 14px;
  font-weight: 600;
  color: #9e80ff;
  display: inline-flex;
  align-items: center;
  transition: color 0.2s ease, transform 0.2s ease;
}

.feature-card:hover .feature-link {
  color: #c2acff;
}

.feature-card:hover .feature-link i {
  transform: translateX(4px);
}

.feature-link i {
  transition: transform 0.2s ease;
}

/* ---------- Bottom CTA ---------- */
.cta-band {
  padding: 30px 15px 80px;
}

.cta-inner {
  background: hsl(254, 90.1%, 68.2%);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 56px 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.cta-inner::before {
  display: none;
}

.cta-title {
  font-family: "Poppins", sans-serif;
  font-weight: 700;
  font-size: 34px;
  color: #fff;
  margin-bottom: 12px;
  position: relative;
}

.cta-text {
  font-size: 17px;
  color: #c8c6d6;
  margin-bottom: 28px;
  position: relative;
}

.cta-buttons {
  display: inline-flex;
  gap: 14px;
  flex-wrap: wrap;
  justify-content: center;
  position: relative;
}

/* ---------- Footer ---------- */
.footer {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 36px 0 24px;
  margin-top: 20px;
}

.footer-line {
  font-size: 14px;
  color: #a8a5bb;
  margin: 8px 0 0;
}

.footer-line a {
  color: #c2acff;
  text-decoration: none;
}

.footer-links {
  display: flex;
  flex-wrap: wrap;
  gap: 14px 18px;
  justify-content: flex-end;
}

.footer_title {
  color: #c8c6d6;
  font-size: 14px;
  text-decoration: none;
  font-weight: 500;
}

.footer_title:hover {
  color: #9e80ff;
  text-decoration: none;
}

/* ---------- Responsive ---------- */
@media screen and (max-width: 1080px) {
  .hero-visual {
    display: none;
  }
  .hero {
    padding: 32px 0 36px;
  }
  .hero-title {
    font-size: 44px;
  }
  .section-title {
    font-size: 32px;
  }
}

@media screen and (max-width: 640px) {
  .hero {
    padding: 20px 0 24px;
  }
  .hero-title {
    font-size: 36px;
  }
  .hero-trust-inline {
    font-size: 16px;
  }
  .cta-inner {
    padding: 40px 24px;
  }
  .cta-title {
    font-size: 26px;
  }
  .footer-links {
    justify-content: flex-start;
    margin-top: 16px;
  }
}
</style>
